import React, { useEffect, useState, useRef } from "react";
import {
  View,
  Animated,
  Easing,
  Pressable,
  StyleSheet,
  Linking,
  LayoutChangeEvent,
} from "react-native";
import RText from "../RText";
import Icon from "../Icon";
import variables from "../../styles/variables";
import { Nav } from "../../client/more/externalWebsites";

type Props = {
  heading: string;
  screens: Nav[];
  navigation: any;
};

const DrawContentItem = ({ heading, screens, navigation }: Props) => {
  const [open, setOpen] = useState(false);
  const [rotateAnim] = useState(new Animated.Value(0));
  const [heightAnim] = useState(new Animated.Value(0));
  const [maxHeight, setMaxHeight] = useState(0);
  const totalHeightRef = useRef(0); // Ref to accumulate total height
  const measuredItemsRef = useRef(0); // Ref to track the number of measured items

  useEffect(() => {
    Animated.timing(rotateAnim, {
      toValue: open ? 1 : 0,
      duration: 250,
      useNativeDriver: false,
      easing: Easing.linear,
    }).start();
    Animated.timing(heightAnim, {
      toValue: open ? 1 : 0,
      duration: 250,
      useNativeDriver: false,
      easing: Easing.linear,
    }).start();
  }, [open]);

  const rotateInterpolate = rotateAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "180deg"],
  });

  const maxHeightInterpolate = heightAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, maxHeight],
  });

  const handleLayout = (e: LayoutChangeEvent) => {
    const itemHeight = e.nativeEvent.layout.height;
    totalHeightRef.current += itemHeight; // Accumulate height
    measuredItemsRef.current += 1; // Increment measured items count

    // Once all items are measured, update maxHeight
    if (measuredItemsRef.current === screens.length) {
      setMaxHeight(totalHeightRef.current);
    }
  };

  return (
    <>
      <Pressable
        onPress={() => {
          if (screens?.length <= 1) {
            navigation.navigate(heading.split(" ").join(""));
          } else {
            setOpen((prev) => !prev);
          }
        }}
        style={styles.row}
      >
        <View style={styles.heading}>
          <RText family="secondary" style={styles.text} weight="semiBold">
            {heading}
          </RText>
        </View>
        <View style={styles.rightAlign}>
          <Animated.View
            style={{
              transform: [{ rotate: rotateInterpolate }],
            }}
          >
            <Icon
              name={
                screens?.length > 1 ? "chevronBoldDown" : "chevronLightRight"
              }
              size="small"
              fill="palette.neutral.x0"
            />
          </Animated.View>
        </View>
      </Pressable>
      {screens?.length > 1 ? (
        <Animated.View
          style={{
            height: maxHeightInterpolate,
            overflow: "hidden",
          }}
        >
          {screens.map((sc, i) => (
            <Pressable
              onLayout={handleLayout}
              onPress={() =>
                sc?.route.startsWith("http")
                  ? Linking.openURL(sc.route)
                  : navigation.navigate(sc.route, sc?.params || {})
              }
              style={styles.row}
              key={`${i}${sc.title}`}
            >
              <View style={styles.flexOne}>
                <RText family="secondary" color="secondary" style={styles.text}>
                  {sc.title}
                </RText>
              </View>
              <View style={styles.rightAlign}>
                <Icon name="chevronLightRight" size="small" />
              </View>
            </Pressable>
          ))}
        </Animated.View>
      ) : null}
    </>
  );
};

const { spacing, colors } = variables;

const styles = StyleSheet.create({
  flexOne: {
    flex: 1,
    paddingLeft: spacing.small,
  },
  heading: { flex: 1, alignItems: "flex-start", justifyContent: "center" },
  rightAlign: {
    flex: 0.3,
    alignItems: "flex-end",
    justifyContent: "center",
  },
  text: {
    textTransform: "uppercase",
  },
  row: {
    flexDirection: "row",
    padding: spacing.xsmall,
    borderTopWidth: 1,
    borderTopColor: colors.palette.neutral.x100,
  },
});

export default DrawContentItem;
